import {
  CheckIcon,
  EllipsisIcon,
  HandIcon,
  LockOpenIcon,
  PencilIcon,
  RefreshCwIcon,
  SendIcon,
  Undo2Icon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

import { OrderAction, OrderStatus, type OrderDTO } from '@invenco/common-interface/sales';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
  AlertDialogWithActionTrigger,
} from '@/components/alert-dialog-with-action';
import { ButtonSkeleton } from '@/components/button-skeleton';
import { Button } from '@/components/core/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import { HeaderActions } from '@/components/header';
import { Result } from '@/shared/helpers/Result';

import { orderActionToManagedAction } from '../order-details/actionDetails';

type OrderHeaderAction = {
  id: 'edit' | OrderAction;
  label: string;
  icon: LucideIcon;
  priority: number;
  menuOnly?: boolean;
  primary?: boolean;
  confirmation?: {
    title: string;
    description: string;
  };
  onSelect: () => Promise<Result>;
};

type Props = {
  order?: OrderDTO;
  isLoading?: boolean;
  onRefresh: () => Promise<unknown>;
  applyManualHold: () => Promise<Result>;
  releaseManualHold: () => Promise<Result>;
  revertToDraft: () => Promise<Result>;
  releaseOrder: () => Promise<Result>;
  cancelOrder: () => Promise<Result>;
  closeOrder: () => Promise<Result>;
};

const MAX_DIRECT_ACTIONS = 2;
const CONFIRMATION_DESCRIPTION =
  'This action cannot be undone, and the order will not be processed';

const orderActionConfig: Record<OrderAction, Omit<OrderHeaderAction, 'id' | 'onSelect'>> = {
  [OrderAction.APPLY_MANUAL_HOLD]: {
    label: 'Hold',
    icon: HandIcon,
    priority: 80,
  },
  [OrderAction.RELEASE_MANUAL_HOLD]: {
    label: 'Release hold',
    icon: LockOpenIcon,
    priority: 60,
    primary: true,
  },
  [OrderAction.REVERT_TO_DRAFT]: {
    label: 'Revert to draft',
    icon: Undo2Icon,
    priority: 70,
  },
  [OrderAction.RELEASE]: {
    label: 'Release',
    icon: SendIcon,
    priority: 90,
    primary: true,
  },
  [OrderAction.CANCEL]: {
    label: 'Cancel',
    icon: XIcon,
    priority: 50,
    menuOnly: true,
    confirmation: {
      title: 'Are you sure you want to cancel the order?',
      description: CONFIRMATION_DESCRIPTION,
    },
  },
  [OrderAction.CLOSE]: {
    label: 'Close',
    icon: CheckIcon,
    priority: 40,
    menuOnly: true,
    confirmation: {
      title: 'Are you sure you want to close the order?',
      description: CONFIRMATION_DESCRIPTION,
    },
  },
};

export function OrderActions({
  order,
  isLoading,
  onRefresh,
  applyManualHold,
  releaseManualHold,
  revertToDraft,
  releaseOrder,
  cancelOrder,
  closeOrder,
}: Props) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [performingAction, setPerformingAction] = useState<OrderHeaderAction['id']>();

  const actionHandlers: Record<OrderAction, () => Promise<Result>> = {
    [OrderAction.APPLY_MANUAL_HOLD]: applyManualHold,
    [OrderAction.RELEASE_MANUAL_HOLD]: releaseManualHold,
    [OrderAction.REVERT_TO_DRAFT]: revertToDraft,
    [OrderAction.RELEASE]: releaseOrder,
    [OrderAction.CANCEL]: cancelOrder,
    [OrderAction.CLOSE]: closeOrder,
  };

  const actions: OrderHeaderAction[] = [];

  if (order?.status === OrderStatus.DRAFT) {
    actions.push({
      id: 'edit',
      label: 'Edit',
      icon: PencilIcon,
      priority: 100,
      // TODO: Wire this to the order edit flow when the shad details page supports editing.
      onSelect: async () => Result.ok(),
    });
  }

  for (const action of order?.availableActions ?? []) {
    const managedAction = orderActionToManagedAction[action];
    if (managedAction && order?.managedActions?.includes(managedAction)) {
      continue;
    }

    actions.push({
      id: action,
      ...orderActionConfig[action],
      onSelect: actionHandlers[action],
    });
  }

  const prioritySort = (a: OrderHeaderAction, b: OrderHeaderAction) => b.priority - a.priority;
  const directActions = actions
    .filter((action) => !action.menuOnly)
    .sort(prioritySort)
    .slice(0, MAX_DIRECT_ACTIONS);
  const directActionIds = new Set(directActions.map((action) => action.id));
  const menuActions = actions
    .filter((action) => action.menuOnly || !directActionIds.has(action.id))
    .sort(prioritySort);
  const primaryActionId = directActions.find((action) => action.primary)?.id;

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const selectAction = async (action: OrderHeaderAction): Promise<Result> => {
    setPerformingAction(action.id);
    try {
      return await action.onSelect();
    } finally {
      setPerformingAction(undefined);
    }
  };

  return (
    <HeaderActions>
      {!isLoading
        ? directActions.map((action) => {
            const Icon = action.icon;
            return (
              <Button
                key={action.id}
                variant={action.id === primaryActionId ? 'default' : 'outline'}
                loading={performingAction === action.id}
                disabled={!!performingAction && performingAction !== action.id}
                onClick={() => void selectAction(action)}
              >
                <Icon />
                {action.label}
              </Button>
            );
          })
        : null}
      {!isLoading && menuActions.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" aria-label="More actions">
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {menuActions.map((action) => {
              const Icon = action.icon;

              if (action.confirmation) {
                return (
                  <AlertDialogWithAction key={action.id} onAction={() => selectAction(action)}>
                    <AlertDialogWithActionTrigger asChild>
                      <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                        <Icon />
                        {action.label}
                      </DropdownMenuItem>
                    </AlertDialogWithActionTrigger>
                    <AlertDialogWithActionContent>
                      <AlertDialogWithActionHeader>
                        <AlertDialogWithActionTitle>
                          {action.confirmation.title}
                        </AlertDialogWithActionTitle>
                        <AlertDialogWithActionDescription>
                          {action.confirmation.description}
                        </AlertDialogWithActionDescription>
                      </AlertDialogWithActionHeader>
                      <AlertDialogWithActionFooter>
                        <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
                        <AlertDialogWithActionSubmit>Confirm</AlertDialogWithActionSubmit>
                      </AlertDialogWithActionFooter>
                    </AlertDialogWithActionContent>
                  </AlertDialogWithAction>
                );
              }

              return (
                <DropdownMenuItem key={action.id} onSelect={() => void selectAction(action)}>
                  <Icon />
                  {action.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button
        size="icon"
        variant="outline"
        aria-label="Refresh"
        loading={isRefreshing}
        disabled={isLoading}
        onClick={() => void refresh()}
      >
        <RefreshCwIcon />
      </Button>
      {isLoading ? (
        <>
          {Array.from({ length: MAX_DIRECT_ACTIONS }, (_, index) => (
            <ButtonSkeleton key={index} data-testid="order-action-skeleton" />
          ))}
        </>
      ) : null}
    </HeaderActions>
  );
}
